import React, { useMemo, useState } from 'react';
import { slotState, SLOT_STATE } from '../../lib/slotState';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Activity, AlertCircle, ArrowRight, AtSign, Globe, History, Layers, LifeBuoy, MonitorSmartphone,
  Plus, RefreshCw, ShieldCheck, Smartphone,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import useDashboardData from '../../hooks/useDashboardData';
import { slotKey } from '../../lib/slotSelection';
import { PRODUCT_NAME } from '../../lib/version';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '@/lib/utils';
import useDoctorMe from '../../hooks/useDoctorMe';
import { doctorUiOn } from '../../lib/doctorSwitch';
import { attentionSummary, anyDoctorData } from '../../lib/doctorView';
import AttentionTile from '../../components/doctor/AttentionTile';

/*
 * Aurora - candidate V3 dashboard (2026-08-23). Premium, warm, spacious.
 *
 * Composition, top to bottom:
 *   greeting + actions  ->  desktop-clients strip (the live presence)  ->  hero: three large
 *   numbers (active slots, phones connected, workers running)  ->  four metric tiles (2 -> 4
 *   columns)  ->  "where the slots live" (slots per phone / per time zone, a real breakdown;
 *   this server has no activity series, see hooks/useDashboardData.js) + system status +
 *   desktop client (1/3 column)  ->  the first six slots (1 -> 2 -> 3 columns) with "view all".
 *
 * Three soft auras sit behind everything. They are rendered through a portal PREPENDED to <body>
 * (not inside <main>): <main> animates on page enter, which would make fixed children position
 * against it for 220ms and then jump; and being first in the body they paint under the page
 * content even while that animation runs. The page root is `relative z-[1]` so every card sits
 * above them; the shell's glass bars (z-30/40) let them glow through.
 *
 * All data comes from useDashboardData(). Honest states: skeleton on first load, a retry card
 * when the first load fails, a slim retry banner when a later refresh fails (data stays on
 * screen), a quiet note when only presence is down, and only the Refresh icon spins on polls.
 */

/* ------------------------------------------------------------------ helpers */

const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};
const fmt = (v) => (typeof v === 'string' && Number.isNaN(Number(v)) ? v : n(v).toLocaleString());
const plural = (count, one, many = `${one}s`) => `${fmt(count)} ${n(count) === 1 ? one : many}`;

function greetingFor(hour) {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function firstNameOf(user) {
  const raw = user?.firstName || user?.first_name || user?.full_name || user?.name || '';
  return String(raw).trim().split(/\s+/)[0] || '';
}

function ago(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function errorText(error) {
  return error?.response?.data?.message || error?.message || 'The server did not answer.';
}

// Running only - a paused slot is not active, and neither is a de-provisioned one.
const isActive = (s) => slotState(s) === SLOT_STATE.RUNNING;
const deviceName = (d) => d?.label || d?.device_name || d?.device_id || '';
const clientName = (c) => c?.label || c?.hostname || c?.installId || 'PC';

/* Tailwind needs every class spelled out, so tones are full strings. Only 5-step opacity values
 * (10, 15, 20, 25 ...) exist in this build - `/12` would be silently dropped. */
const TONE = {
  primary: { chip: 'bg-primary/15 text-primary ring-primary/25', dot: 'bg-primary', head: 'from-primary/20 via-primary/5 to-transparent', rule: 'from-primary via-primary/30 to-transparent' },
  violet:  { chip: 'bg-chart-2/15 text-chart-2 ring-chart-2/25', dot: 'bg-chart-2', head: 'from-chart-2/20 via-chart-2/5 to-transparent', rule: 'from-chart-2 via-chart-2/30 to-transparent' },
  success: { chip: 'bg-success/15 text-success ring-success/25', dot: 'bg-success', head: 'from-success/20 via-success/5 to-transparent', rule: 'from-success via-success/30 to-transparent' },
  warning: { chip: 'bg-warning/15 text-warning ring-warning/25', dot: 'bg-warning', head: 'from-warning/20 via-warning/5 to-transparent', rule: 'from-warning via-warning/30 to-transparent' },
  danger:  { chip: 'bg-danger/15 text-danger ring-danger/25', dot: 'bg-danger', head: 'from-danger/20 via-danger/5 to-transparent', rule: 'from-danger via-danger/30 to-transparent' },
  muted:   { chip: 'bg-muted/60 text-muted-foreground ring-border', dot: 'bg-muted-foreground', head: 'from-muted/60 via-muted/20 to-transparent', rule: 'from-muted-foreground via-muted-foreground/30 to-transparent' },
};

const STATUS = {
  [SLOT_STATE.RUNNING]: ['Running', 'success'],
  [SLOT_STATE.PAUSED]: ['Paused', 'warning'],
  [SLOT_STATE.NOT_PROVISIONED]: ['Not provisioned', 'muted'],
};
function statusOf(slot) {
  // Keyed on the derived state, not on a raw field: the old code read `slot.status`, a synthesised
  // alias of the ADMIN capacity bit that the server no longer sends at all.
  const [label, tone] = STATUS[slotState(slot)] || ['Running', 'success'];
  return { label, tone };
}

const EASE = [0.2, 0.7, 0.2, 1];

/* Fade-up with a 40ms stagger; nothing moves when the OS asks for reduced motion. */
function useRise() {
  const reduce = useReducedMotion();
  const rise = (i = 0) => (reduce
    ? {}
    : {
      initial: { opacity: 0, y: 10 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.28, ease: EASE, delay: 0.04 * i },
    });
  return { rise, reduce };
}

/* ------------------------------------------------------------------ pieces */

function Headline({ label, value, of, sub, tone = 'primary', to, testId }) {
  const body = (
    <>
      <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 leading-none">
        <span className="font-heading text-4xl font-bold tracking-tight tnum sm:text-5xl">{fmt(value)}</span>
        {of && <span className="text-base font-medium text-muted-foreground/70 sm:text-lg">{of}</span>}
      </p>
      <span className={cn('mt-4 block h-px w-full bg-gradient-to-r', TONE[tone].rule)} aria-hidden />
      {sub && <p className="mt-3 text-sm text-muted-foreground">{sub}</p>}
    </>
  );
  const cls = 'block min-w-0 rounded-xl';
  return to
    ? <Link to={to} className={cn(cls, 'tap')} data-testid={testId}>{body}</Link>
    : <div className={cls} data-testid={testId}>{body}</div>;
}

function Tile({ icon: Icon, tone, label, value, sub, to, testId, ...motionProps }) {
  const inner = (
    <>
      <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1', TONE[tone].chip)}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0">
        <p className="truncate font-heading text-2xl font-semibold leading-none tracking-tight tnum sm:text-[1.75rem]">{fmt(value)}</p>
        <p className="mt-2 truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {sub && <p className="mt-0.5 truncate text-xs text-muted-foreground/70">{sub}</p>}
      </div>
    </>
  );
  const cls = 'aurora-card flex min-w-0 flex-col gap-4 p-4 sm:p-5';
  return to ? (
    <motion.div {...motionProps} className="min-w-0">
      <Link to={to} className={cn(cls, 'tap h-full')} data-testid={testId}>{inner}</Link>
    </motion.div>
  ) : (
    <motion.div {...motionProps} className={cls} data-testid={testId}>{inner}</motion.div>
  );
}

function PillToggle({ options, value, onChange, reduce, label }) {
  return (
    <div role="tablist" aria-label={label} className="inline-flex self-start rounded-full bg-secondary/70 p-1 ring-1 ring-border">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            data-testid={`toggle-${o.value}`}
            className={cn(
              'tap relative rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors',
              on ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {on && (
              <motion.span
                layoutId={`aurora-pill-${label}`}
                className="absolute inset-0 rounded-full bg-primary shadow-glow"
                transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 40 }}
                aria-hidden
              />
            )}
            <span className="relative">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* A bar per group: the full bar is the slot count, the bright part of it the active ones. */
function BarList({ rows, reduce, emptyTitle, emptyText }) {
  if (!rows.length) {
    return (
      <div className="flex min-h-[12rem] flex-col items-center justify-center text-center sm:min-h-[16rem]">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
          <Layers className="h-5 w-5" />
        </span>
        <p className="mt-3 text-sm font-medium">{emptyTitle}</p>
        <p className="mt-0.5 max-w-xs text-xs text-muted-foreground">{emptyText}</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  const spring = reduce ? { duration: 0 } : { type: 'spring', stiffness: 260, damping: 34 };
  return (
    <ol className="mt-5 space-y-4">
      {rows.map((r) => (
        <li key={r.key} className="min-w-0">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate font-medium">{r.name}</span>
            <span className="shrink-0 text-muted-foreground tnum">
              <span className="text-foreground">{fmt(r.active)}</span> active · {plural(r.count, 'slot')}
            </span>
          </div>
          <div className="relative mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary/80">
            <motion.span
              className="absolute inset-y-0 left-0 rounded-full bg-primary/25"
              initial={false}
              animate={{ width: `${(r.count / max) * 100}%` }}
              transition={spring}
            />
            <motion.span
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary to-chart-2"
              initial={false}
              animate={{ width: `${(r.active / max) * 100}%` }}
              transition={spring}
            />
          </div>
        </li>
      ))}
    </ol>
  );
}

function Fact({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <dt className="flex items-center gap-2.5 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </dt>
      <dd className="text-right text-sm font-semibold tnum">{children}</dd>
    </div>
  );
}

function Of({ children }) {
  return <span className="font-normal text-muted-foreground">{children}</span>;
}

function SlotCard({ slot, phone, ...motionProps }) {
  const key = slotKey(slot);
  const { label, tone } = statusOf(slot);
  return (
    <motion.article {...motionProps} className="aurora-card flex min-w-0 flex-col overflow-hidden" data-testid={`slot-card-${key || slot.id}`}>
      <div className={cn('bg-gradient-to-br px-4 pb-3 pt-4 sm:px-5', TONE[tone].head)}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-semibold">{slot.slot_label || key || 'Unnamed slot'}</p>
            <p className="truncate text-sm text-muted-foreground">{slot.ig_username ? `@${slot.ig_username}` : 'No account linked'}</p>
          </div>
          <span className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1', TONE[tone].chip)}>
            <span className={cn('h-1.5 w-1.5 rounded-full', TONE[tone].dot, tone === 'success' && 'animate-pulse')} aria-hidden />
            {label}
          </span>
        </div>
      </div>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 px-4 pb-4 pt-2 xs:grid-cols-2 sm:px-5">
        <div className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Phone</dt>
          <dd className={cn('truncate text-sm font-medium', !phone && 'text-muted-foreground')}>{phone || 'Unassigned'}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Time zone</dt>
          <dd className={cn('truncate text-sm font-medium', !slot.timezone && 'text-muted-foreground')}>{slot.timezone || 'Not set'}</dd>
        </div>
      </dl>
      <div className="mt-auto flex items-center justify-between gap-2 border-t border-border/60 px-4 py-1.5 text-xs sm:px-5">
        <span className="truncate font-mono text-muted-foreground/80">{key}</span>
        <div className="flex shrink-0 items-center">
          {slot.slot_id && (
            <Link to={`/slots/${slot.slot_id}/stats`} className="tap inline-flex items-center rounded-lg px-2.5 py-1.5 font-medium text-muted-foreground transition-colors hover:text-foreground">
              Stats
            </Link>
          )}
          {key && (
            <Link to={`/slots/${encodeURIComponent(key)}/settings`} className="tap inline-flex items-center rounded-lg px-2.5 py-1.5 font-medium text-muted-foreground transition-colors hover:text-foreground">
              Settings
            </Link>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function EmptySlots() {
  return (
    <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="slots-empty">
      <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
        <Layers className="h-6 w-6" />
      </span>
      <h3 className="mt-4">No slots yet</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
        A slot is one Instagram account on one phone. Create your first and the fleet starts here.
      </p>
      <Button asChild className="tap mt-5 rounded-full">
        <Link to="/slots"><Plus className="h-4 w-4" /> Create a slot</Link>
      </Button>
    </div>
  );
}

function FirstLoadError({ error, onRetry }) {
  return (
    <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="dashboard-error">
      <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
        <AlertCircle className="h-6 w-6" />
      </span>
      <h3 className="mt-4">Could not reach the server</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">{errorText(error)}</p>
      <Button variant="outline" onClick={onRetry} className="tap mt-5 rounded-full">
        <RefreshCw className="h-4 w-4" /> Try again
      </Button>
    </div>
  );
}

function RefreshError({ error, onRetry }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm" role="alert">
      <AlertCircle className="h-4 w-4 shrink-0 text-danger" />
      <p className="min-w-0 flex-1">
        <span className="font-medium">Last refresh failed.</span>{' '}
        <span className="text-muted-foreground">{errorText(error)} Showing the last good numbers.</span>
      </p>
      <Button variant="outline" size="sm" onClick={onRetry} className="tap rounded-full">Retry</Button>
    </div>
  );
}

function AuroraSkeleton() {
  return (
    <div className="space-y-6 sm:space-y-8" aria-busy="true" aria-label="Loading dashboard" data-testid="dashboard-skeleton">
      <div className="aurora-card p-5 sm:p-7 lg:p-8">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-8">
          {[0, 1, 2].map((i) => (
            <div key={i}>
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-3 h-10 w-32 sm:h-12" />
              <Skeleton className="mt-4 h-px w-full" />
              <Skeleton className="mt-3 h-3 w-28" />
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="aurora-card p-4 sm:p-5">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="mt-4 h-7 w-20" />
            <Skeleton className="mt-2 h-3 w-16" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <div className="aurora-card p-5 sm:p-6 lg:col-span-2 lg:self-start">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-2 h-3 w-24" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="mt-5">
              <div className="flex justify-between"><Skeleton className="h-3 w-28" /><Skeleton className="h-3 w-20" /></div>
              <Skeleton className="mt-2 h-1.5 w-full rounded-full" />
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-6 lg:gap-8">
          <div className="aurora-card p-5 sm:p-6">
            <Skeleton className="h-4 w-28" />
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="mt-4 h-4 w-full" />)}
          </div>
          <div className="aurora-card p-5 sm:p-6">
            <Skeleton className="h-11 w-11 rounded-full" />
            <Skeleton className="mt-4 h-4 w-32" />
            <Skeleton className="mt-2 h-3 w-full" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 w-full rounded-2xl" />)}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ the page */

const GROUPINGS = [
  { value: 'phone', label: 'By phone' },
  { value: 'zone', label: 'By time zone' },
];

export default function Dashboard() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const data = useDashboardData();
  const { rise, reduce } = useRise();
  const [grouping, setGrouping] = useState('phone');
  // r16sd Settings Doctor (lib/doctorSwitch.js part dashboardTile): the "Needs attention" tile from the slot list's attention (no
  // request of its own). Drawn only while the server's doctor is on.
  const doctor = useDoctorMe();
  const showAttention = doctorUiOn('dashboardTile') && (doctor.available || anyDoctorData(data.slots));
  const attention = useMemo(() => (showAttention ? attentionSummary(data.slots) : null), [showAttention, data.slots]);

  const name = firstNameOf(user);
  const hello = greetingFor(new Date().getHours());
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const appName = branding?.appName || PRODUCT_NAME;
  const isAdmin = user?.role === 'owner' || !!user?.isAdmin;

  const {
    slots, devices, clients, activeSlots, inactiveSlots, slotsWithAccount, devicesInUse, clientsOnline,
    phonesConnected, workersRunning, slotsByDevice, timezones, totalSlots, totalDevices, totalClients, openTickets,
  } = data;

  const devicesById = {};
  devices.forEach((d) => { if (d.device_id) devicesById[d.device_id] = d; });
  const phoneFor = (slot) => (slot.device_id ? deviceName(devicesById[slot.device_id]) || slot.device_id : '');

  const group = (keyOf, nameOf) => {
    const map = {};
    slots.forEach((s) => {
      const k = keyOf(s);
      (map[k] = map[k] || { key: k, name: nameOf(k), count: 0, active: 0 }).count += 1;
      if (isActive(s)) map[k].active += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  };
  const rows = grouping === 'phone'
    ? group((s) => s.device_id || 'unassigned', (k) => (k === 'unassigned' ? 'Unassigned' : deviceName(devicesById[k]) || k))
    : group((s) => s.timezone || 'none', (k) => (k === 'none' ? 'No time zone' : k));
  const shownRows = rows.slice(0, 8);
  const unassigned = slotsByDevice.unassigned?.length || 0;
  const liveClients = clients.slice(0, 6);

  return (
    <div className="relative z-[1] space-y-6 sm:space-y-8" data-testid="dashboard-aurora">
      

      {/* ---------------------------------------------------------- greeting */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{today}</p>
          <h1 className="mt-1 text-[1.75rem] font-bold tracking-tight text-balance sm:text-[2.125rem]" data-testid="greeting">
            {hello}{name ? `, ${name}` : ''}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Here is how your fleet stands right now.</p>
        </div>
        <div className="flex items-center gap-2 sm:shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => data.refetch()}
            className="tap rounded-full"
            title={data.refreshing ? 'Refreshing in the background - the page stays put' : 'Refresh now (auto-refreshes every 30 s)'}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn('h-4 w-4', data.refreshing && 'animate-spin text-primary')} />
            Refresh
          </Button>
          <Button asChild size="sm" className="tap rounded-full shadow-glow">
            <Link to="/slots"><Plus className="h-4 w-4" /> New slot</Link>
          </Button>
        </div>
      </header>

      {data.firstLoad ? (
        data.error ? <FirstLoadError error={data.error} onRetry={data.refetch} /> : <AuroraSkeleton />
      ) : (
        <>
          {data.error && <RefreshError error={data.error} onRetry={data.refetch} />}

          {/* ------------------------------------------------------ desktop clients (presence) */}
          {clients.length > 0 && (
            <motion.section {...rise(0)} className={cn('aurora-card p-4 sm:p-5', clientsOnline.length > 0 && 'aurora-card-live')} data-testid="clients-strip">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  {clientsOnline.length > 0 ? (
                    <span className="relative flex h-2.5 w-2.5" aria-hidden>
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
                    </span>
                  ) : (
                    <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/60" aria-hidden />
                  )}
                  <p className="text-sm font-medium">
                    {clientsOnline.length > 0
                      ? `${plural(clientsOnline.length, 'desktop client')} online · ${plural(phonesConnected, 'phone')} · ${plural(workersRunning, 'worker')}`
                      : 'No desktop client connected right now'}
                  </p>
                </div>
                <Link to="/monitor" className="tap inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">
                  Live monitor <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <ul className="mt-3 flex flex-wrap gap-2">
                {liveClients.map((c, i) => {
                  const on = !!c.client_connected;
                  return (
                    <li
                      key={c.installId || c.hostname || `${clientName(c)}-${i}`}
                      className={cn('inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1.5 text-xs ring-1', on ? 'bg-success/10 ring-success/20' : 'bg-muted/40 ring-border')}
                    >
                      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', on ? 'bg-success' : 'bg-muted-foreground/60')} aria-hidden />
                      <span className="truncate font-medium">{clientName(c)}</span>
                      <span className="shrink-0 text-muted-foreground tnum">
                        {on
                          ? `${plural(c.devices?.length, 'phone')} · ${plural(c.workers?.length, 'worker')}`
                          : `offline${ago(c.lastSeen) ? ` · ${ago(c.lastSeen)}` : ''}`}
                      </span>
                    </li>
                  );
                })}
                {clients.length > liveClients.length && (
                  <li className="inline-flex items-center rounded-full px-3 py-1.5 text-xs text-muted-foreground">
                    +{clients.length - liveClients.length} more
                  </li>
                )}
              </ul>
            </motion.section>
          )}
          {data.presenceError && clients.length === 0 && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="presence-unavailable">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              Desktop-client presence is unavailable right now; phones and workers read 0 until it is back.
            </p>
          )}

          {/* ------------------------------------------------------ hero */}
          <motion.section {...rise(1)} className="aurora-card aurora-hero p-5 sm:p-7 lg:p-8" data-testid="hero">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-8">
              <Headline
                label="Active slots"
                value={activeSlots.length}
                of={`of ${fmt(totalSlots)}`}
                sub={`${fmt(inactiveSlots.length)} idle · ${fmt(slotsWithAccount.length)} with an account`}
                tone="primary"
                to="/slots"
                testId="hero-active-slots"
              />
              <Headline
                label="Phones connected"
                value={phonesConnected}
                sub={`${plural(totalDevices, 'phone')} registered · ${fmt(devicesInUse.length)} in use`}
                tone="violet"
                to="/devices"
                testId="hero-phones"
              />
              <Headline
                label="Workers running"
                value={workersRunning}
                sub={`${fmt(clientsOnline.length)} of ${plural(totalClients, 'desktop client')} online`}
                tone="success"
                to="/monitor"
                testId="hero-workers"
              />
            </div>
          </motion.section>

          {/* ------------------------------------------------------ r16sd: needs attention */}
          {attention && (
            <motion.div {...rise(2)}>
              <AttentionTile summary={attention} />
            </motion.div>
          )}

          {/* ------------------------------------------------------ metric tiles */}
          <section className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4" aria-label="Fleet metrics">
            <Tile {...rise(2)} icon={Smartphone} tone="violet" label="Devices" value={totalDevices} sub={`${fmt(devicesInUse.length)} in use`} to="/devices" testId="tile-devices" />
            <Tile {...rise(3)} icon={AtSign} tone="primary" label="With account" value={slotsWithAccount.length} sub={`${fmt(Math.max(0, totalSlots - slotsWithAccount.length))} without`} to="/slots" testId="tile-accounts" />
            <Tile {...rise(4)} icon={Globe} tone="success" label="Time zones" value={timezones.length} sub={timezones.length ? timezones.slice(0, 2).join(', ') : 'none set yet'} testId="tile-timezones" />
            <Tile
              {...rise(5)}
              icon={LifeBuoy}
              tone={n(openTickets) > 0 ? 'warning' : 'muted'}
              label="Open tickets"
              value={openTickets == null ? 'Off' : openTickets}
              sub={openTickets == null ? 'support queue disabled' : 'in the support queue'}
              to={isAdmin && openTickets != null ? '/admin/support' : undefined}
              testId="tile-tickets"
            />
          </section>

          {/* ------------------------------------------------------ breakdown + side column
           * The breakdown sizes to its bars (lg:self-start) instead of stretching to the side
           * column's height; with two or three phones it would otherwise be mostly empty glass. */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            <motion.section {...rise(6)} className="aurora-card p-5 sm:p-6 lg:col-span-2 lg:self-start" data-testid="fleet-breakdown">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-base font-semibold">Where the slots live</h2>
                  <p className="text-sm text-muted-foreground">
                    {grouping === 'phone' ? 'Slots per phone, the bright part active' : 'Slots per time zone, the bright part active'}
                  </p>
                </div>
                <PillToggle options={GROUPINGS} value={grouping} onChange={setGrouping} reduce={reduce} label="Group slots" />
              </div>
              <BarList
                rows={shownRows}
                reduce={reduce}
                emptyTitle="No slots yet"
                emptyText="Once you create slots they appear here grouped by the phone they run on."
              />
              {rows.length > shownRows.length && (
                <Link to={grouping === 'phone' ? '/devices' : '/slots'} className="tap mt-4 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">
                  All {fmt(rows.length)} {grouping === 'phone' ? 'phones' : 'time zones'} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </motion.section>

            <div className="flex flex-col gap-6 lg:gap-8">
              <motion.section {...rise(7)} className="aurora-card p-5 sm:p-6" data-testid="system-status">
                <h2 className="text-base font-semibold">System status</h2>
                <p className="text-sm text-muted-foreground">Right now</p>
                <dl className="mt-3 divide-y divide-border/70">
                  <Fact icon={MonitorSmartphone} label="Clients online">{fmt(clientsOnline.length)} <Of>of {fmt(totalClients)}</Of></Fact>
                  <Fact icon={Activity} label="Workers running">{fmt(workersRunning)}</Fact>
                  <Fact icon={Smartphone} label="Phones in use">{fmt(devicesInUse.length)} <Of>of {fmt(totalDevices)}</Of></Fact>
                  <Fact icon={Layers} label="Unassigned slots">{fmt(unassigned)}</Fact>
                  <Fact icon={LifeBuoy} label="Support queue">{openTickets == null ? <Of>Off</Of> : plural(openTickets, 'open ticket')}</Fact>
                  <Fact icon={ShieldCheck} label="Role">
                    <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 capitalize text-primary">{user?.role || 'user'}</Badge>
                  </Fact>
                </dl>
              </motion.section>

              <motion.section {...rise(8)} className="aurora-card aurora-card-client p-5 sm:p-6" data-testid="desktop-client-card">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/25">
                    <MonitorSmartphone className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold">Desktop client</h2>
                    <p className="mt-1 text-sm text-muted-foreground text-pretty">
                      Run the {appName} desktop client on your PC to connect phones. Builds and release notes live under Versions.
                    </p>
                  </div>
                </div>
                <Button asChild variant="secondary" className="tap mt-4 w-full rounded-full">
                  <Link to="/versions"><History className="h-4 w-4" /> Open Versions <ArrowRight className="h-4 w-4" /></Link>
                </Button>
              </motion.section>
            </div>
          </div>

          {/* ------------------------------------------------------ slots */}
          <section data-testid="slots-section">
            <motion.div {...rise(9)} className="mb-4 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">Slots</h2>
                <p className="text-sm text-muted-foreground">
                  {slots.length > 0 ? `${fmt(activeSlots.length)} of ${fmt(totalSlots)} active` : 'Nothing running yet'}
                </p>
              </div>
              <Link to="/slots" className="tap inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary/80" data-testid="view-all-slots">
                View all <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
            {slots.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {slots.slice(0, 6).map((slot, i) => (
                  <SlotCard key={slot.id || slotKey(slot) || i} slot={slot} phone={phoneFor(slot)} {...rise(10 + i)} />
                ))}
              </div>
            ) : (
              <EmptySlots />
            )}
          </section>
        </>
      )}
    </div>
  );
}
